import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  InboundCampaignComposeRequest,
  InboundCampaignHistoricalRequest,
  InboundCampaignListRequest,
  InboundCampaignRow,
  RunningDidRow,
} from './types';

const POLL_MS = 30_000;

export const inboundKeys = {
  all: ['inbound-campaigns'] as const,
  live: (userId: string | number | null, filter: { campaignName: string; did: string }) =>
    ['inbound-campaigns', 'live', userId, filter] as const,
  adminLive: (userId: string | number | null, filter: { campaignName: string; did: string }) =>
    ['inbound-campaigns', 'admin-live', userId, filter] as const,
  runningDids: ['inbound-campaigns', 'running-dids'] as const,
  clone: (campaignId: string | number) =>
    ['inbound-campaigns', 'clone', campaignId] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: inboundKeys.all });
}

/* ---------------- Lists ---------------- */

/** POST /api/obd/inbound/campaign — USER live inbound campaigns. */
export function useLiveInboundCampaigns(filter: {
  campaignName: string;
  did: string;
}) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: inboundKeys.live(userId, filter),
    queryFn: () =>
      api.post<InboundCampaignRow[]>('/api/obd/inbound/campaign', {
        userId: Number(userId),
        campaignName: filter.campaignName,
        did: filter.did,
      } as InboundCampaignListRequest),
    enabled: Boolean(userId),
    staleTime: 15_000,
    refetchInterval: POLL_MS,
    select: (data) => ensureArray<InboundCampaignRow>(data),
  });
}

/** POST /api/obd/inbound/campaign/historical — date-range filter. */
export function useHistoricalInboundCampaigns() {
  return useMutation({
    mutationFn: (req: InboundCampaignHistoricalRequest) =>
      api
        .post<InboundCampaignRow[]>('/api/obd/inbound/campaign/historical', req)
        .then((res) => ensureArray<InboundCampaignRow>(res)),
  });
}

/** POST /api/obd/superadmin/inbound/campaign/list — admin "all owned users" view. */
export function useAdminLiveInboundCampaigns(filter: {
  campaignName: string;
  did: string;
}) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: inboundKeys.adminLive(userId, filter),
    queryFn: () =>
      api.post<InboundCampaignRow[]>(
        '/api/obd/superadmin/inbound/campaign/list',
        {
          userId: Number(userId),
          campaignName: filter.campaignName,
          did: filter.did,
        } as InboundCampaignListRequest,
      ),
    enabled: Boolean(userId),
    staleTime: 15_000,
    refetchInterval: POLL_MS,
    select: (data) => ensureArray<InboundCampaignRow>(data),
  });
}

/* ---------------- Lifecycle actions ---------------- */

interface CampaignIdRequest {
  campaignId: number;
}

function useActionMutation(endpoint: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: CampaignIdRequest) =>
      api.post<{ message?: string }>(endpoint, req),
    onSuccess: () => invalidate(),
  });
}

export const useInboundCampaignStop = () =>
  useActionMutation('/api/obd/inbound/campaign/stop');
export const useInboundCampaignStart = () =>
  useActionMutation('/api/obd/inbound/campaign/start');
/** Shared with voice — same endpoint. */
export const useInboundCampaignPause = () =>
  useActionMutation('/api/obd/campaign/pause');
export const useInboundCampaignResume = () =>
  useActionMutation('/api/obd/campaign/resume');

/* ---------------- Compose / Clone ---------------- */

/** GET /api/obd/inbound/running/dids — DIDs already in use by other inbound campaigns. */
export function useRunningDids() {
  return useQuery({
    queryKey: inboundKeys.runningDids,
    queryFn: () => api.get<RunningDidRow[]>('/api/obd/inbound/running/dids'),
    staleTime: 30_000,
    select: (data) => ensureArray<RunningDidRow>(data),
  });
}

/** GET /api/obd/inbound/campaign/clone/{id} — fetch existing campaign for cloning. */
export function useInboundCampaignClone(campaignId: string | number | null) {
  return useQuery({
    queryKey: inboundKeys.clone(campaignId ?? 'none'),
    queryFn: () =>
      api.get<unknown>(`/api/obd/inbound/campaign/clone/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** POST /api/obd/inbound/campaign/compose — create or clone-as-new. */
export function useComposeInboundCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: InboundCampaignComposeRequest) =>
      api.post<{ message?: string }>('/api/obd/inbound/campaign/compose', req),
    onSuccess: () => invalidate(),
  });
}
